import type { NewRelicConfig, ValidatedNewRelicConfig } from './types';
import {
  Ajax,
  JSErrors,
  Logging,
  PageViewEvent,
  PageViewTiming,
  SessionReplay,
  SessionTrace,
  Spa,
} from '@newrelic/browser-agent';

const DEFAULT_ENV = 'unknown';
const DEFAULT_BEACON = 'bam.nr-data.net';

export function validateConfig(
  config: NewRelicConfig
): ValidatedNewRelicConfig | null {
  if (process.env.NODE_ENV === 'test') {
    console.warn('[NewRelic] Disabled in test environment');
    return null;
  }

  if (!config.licenseKey || !config.applicationId) {
    console.warn(
      '[NewRelic] Invalid configuration: missing licenseKey or applicationId'
    );
    return null;
  }

  const enabled = config.enabled !== undefined ? config.enabled : true;
  if (!enabled) {
    console.warn('[NewRelic] Disabled by configuration');
    return null;
  }

  return {
    licenseKey: config.licenseKey,
    applicationId: config.applicationId,
    enabled: true,
    environment: config.environment || DEFAULT_ENV,
    ajaxDenyList: config.ajaxDenyList || [],
  };
}

export function createBrowserAgentConfig(config: ValidatedNewRelicConfig) {
  return {
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true },
      ajax: {
        enabled: true,
        autoStart: true,
        deny_list: [...config.ajaxDenyList],
      },
      jserrors: { enabled: true, autoStart: true },
      metrics: { enabled: true, autoStart: true },
      spa: { enabled: true, autoStart: true },
      page_view_timing: { enabled: true, autoStart: true },
      session_replay: {
        enabled: true,
        autoStart: true,
        sampling_rate: 0,
        error_sampling_rate: 1,
      },
    },
    info: {
      beacon: DEFAULT_BEACON,
      errorBeacon: DEFAULT_BEACON,
      licenseKey: config.licenseKey,
      applicationID: config.applicationId,
      jsAttributes: {
        environment: config.environment,
      },
      sa: 1,
    },
    loader_config: {
      agentID: config.applicationId,
      licenseKey: config.licenseKey,
      applicationID: config.applicationId,
    },
    features: [
      Logging,
      JSErrors,
      Ajax,
      SessionTrace,
      SessionReplay,
      Spa,
      PageViewEvent,
      PageViewTiming,
    ],
  };
}
